import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { loginApi } from '../../services/api';
import { encryptMD5 } from '../../utils/crypto';
import logoSvg from './CEAA2016-1998-4755-AE53-87F24E4E38EA.svg';
import './styles.css';

interface LoginFormValues {
  account: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    try {
      const savedAccount = localStorage.getItem('savedAccount');
      if (savedAccount) {
        form.setFieldsValue({ 
          account: savedAccount,
          remember: true
        });
      }
    } catch (error) {
      console.error('Error loading saved credentials:', error);
    }
  }, [form]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const onFinish = async (values: LoginFormValues) => {
    try {
      setLoading(true);
      const loginData = { ...values };
      loginData.password = encryptMD5(values.password);
      const response = await loginApi(loginData.account, loginData.password);
      
      if (response.data.code === 0 && response.data.data) {
        const { access_token, uid, realName } = response.data.data;
        try {
          localStorage.setItem('token', access_token);
          localStorage.setItem('user', JSON.stringify({ uid, realName }));
          if (values.remember) {
            localStorage.setItem('savedAccount', values.account);
          } else {
            localStorage.removeItem('savedAccount');
          }
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }
        messageApi.success('登录成功！');
        navigate('/projects');
      } else {
        messageApi.error(response.data.msg || '登录失败');
      }
    } catch (error) {
      console.error('登录错误:', error);
      messageApi.error('登录失败，请检查网络连接后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.stopPropagation();
    messageApi.info('请联系系统管理员重置密码');
  };

  return (
    <div className="login-container">
      {contextHolder}
      {/* Floating background orbs */}
      <div className="bg-orb orb-1" />
      <div className="bg-orb orb-2" />
      <div className="bg-orb orb-3" />

      <div className="login-card">
        {/* Logo area */}
        <div className="login-logo-area">
          <img src={logoSvg} alt="Logo" className="logo-icon" />
          <h1 className="login-title">综合数据管理系统</h1>
          <p className="login-subtitle">Integrated Data Management System</p>
        </div>

        {/* Login form */}
        <Form
          form={form}
          name="login"
          initialValues={{ remember: false }}
          onFinish={onFinish}
          size="large"
          layout="vertical"
          autoComplete="off"
          className="apple-form"
        >
          <div className="input-group">
            <Form.Item
              name="account"
              rules={[{ required: true, message: '请输入账号' }]}
              className="no-margin"
            >
              <Input 
                prefix={<UserOutlined className="input-icon" />}
                placeholder="账号" 
                autoComplete="off"
                onChange={handleInputChange}
                onPaste={(e) => e.stopPropagation()}
                className="apple-input"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: '请输入密码' }]}
              className="no-margin"
            >
              <Input.Password
                prefix={<LockOutlined className="input-icon" />}
                placeholder="密码"
                autoComplete="off"
                onChange={handleInputChange}
                onPaste={(e) => e.stopPropagation()}
                className="apple-input"
              />
            </Form.Item>
          </div>

          <div className="form-options">
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <label className="checkbox-label">
                <span className="custom-checkbox">
                  <input type="checkbox" />
                  <span className="checkmark"></span>
                </span>
                记住账号
              </label>
            </Form.Item>
            <a className="forgot-link" onClick={handleForgotPassword}>
              忘记密码？
            </a>
          </div>

          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            block
            className="apple-button"
          >
            登录
          </Button>
        </Form>

        {/* Footer */}
        <div className="login-footer">
          <span>© 2026 数据管理系统</span>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
            辽ICP备12014462号-7
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
